/**
 * Imports the CSV exported by AndroMoney.
 *
 * Assumed format (classic Android app export, tolerant of variations):
 *
 *   Id,Currency,Amount,Category,Sub-Category,Date,Expense(Transfer Out),
 *   Income(Transfer In),Note,Periodic,Project,Payee/Payer,uid,Time
 *
 *   - The first line may be a preamble ("AndroMoney", version…); the actual
 *     header is located with `isAndroMoneyHeader` among the first rows.
 *   - `Date` comes as YYYYMMDD (YYYY-MM-DD is also tolerated); `Time` comes as
 *     HHMM or HH:MM and is optional.
 *   - Category `SYSTEM` + subcategory `INIT_AMOUNT` = initial balance for the
 *     account named in the Income column (Expense is tolerated too).
 *   - A row with an account in Expense AND Income = transfer between accounts.
 *   - Expense only → expense; Income only → income; neither → invalid row.
 *
 * The parser is pure: it returns a typed plan (accounts with currency,
 * categories with subcategories, normalized transactions, and per-row errors).
 * Plan execution (`runAndroMoneyImport`) writes to local SQLite inside a
 * drizzle transaction.
 */
import { category, type PowerSyncDrizzleDB, project, tx, wallet } from "@cashsync/powersync";
import { toMinorUnits } from "../../utils/currency";
import { nowISO } from "../../utils/date-helpers";
import { generateId } from "@fleet/utils";
import type { CsvImportIssue } from "./csv";

export interface AndroMoneyRow {
  currency: string;
  /** Amount in minor units. */
  amount: number;
  category: string;
  subCategory: string | null;
  /** YYYY-MM-DD. */
  date: string;
  /** HH:MM (or null if the Time column is missing or invalid). */
  time?: string | null;
  /** Normalized transaction type. */
  kind: "expense" | "income" | "transfer";
  /** Source account (expense or transfer). */
  expenseAccount: string | null;
  /** Destination account (income or transfer). */
  incomeAccount: string | null;
  note: string | null;
  project: string | null;
  payee: string | null;
}

/** Account detected in the export (currency from the first row that uses it). */
export interface AndroMoneyAccount {
  name: string;
  currency: string;
  /** Sum of SYSTEM/INIT_AMOUNT rows, in `currency` minor units. */
  initialBalanceMinor: number;
}

/** Category detected with its subcategories (kind according to row type). */
export interface AndroMoneyCategory {
  name: string;
  kind: "expense" | "income";
  subcategories: string[];
}

export interface AndroMoneyImportPlan {
  rows: AndroMoneyRow[];
  issues: CsvImportIssue[];
  walletsToCreate: string[];
  categoriesToCreate: string[];
  projectsToCreate: string[];
  /** Detected accounts with currency and accumulated initial balance. */
  accounts: AndroMoneyAccount[];
  /** Detected categories (with subcategories), excluding SYSTEM. */
  categories: AndroMoneyCategory[];
}

/** Detects whether a CSV header looks like an AndroMoney export. */
export function isAndroMoneyHeader(headerCells: readonly string[]): boolean {
  const wanted = ["currency", "amount", "category", "sub-category"];
  const normalized = new Set(headerCells.map((c) => c.trim().toLowerCase()));
  return wanted.every((c) => normalized.has(c));
}

// ─── CSV parsing ──────────────────────────────────────────────────────────────

/**
 * Parses a complete CSV document (RFC 4180: quotes, escaped quotes, line breaks
 * inside quotes, CRLF). Comma separator.
 */
export function parseCsvDocument(text: string): string[][] {
  const records: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let sawAny = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    pushField();
    records.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    sawAny = true;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      pushField();
    } else if (ch === "\n") {
      pushRow();
    } else if (ch === "\r") {
      if (text[i + 1] === "\n") {
        i++;
      }
      pushRow();
    } else {
      field += ch;
    }
  }
  if (sawAny && (field.length > 0 || row.length > 0)) {
    pushRow();
  }
  return records;
}

/** Removes the UTF-8 BOM if present (basic encoding detection). */
export function stripBom(text: string): string {
  return text.codePointAt(0) === 0xfe_ff ? text.slice(1) : text;
}

// ─── Cell normalization ────────────────────────────────────────────────────────

const ANDRO_SYSTEM_CATEGORY = "SYSTEM";
const ANDRO_INIT_SUBCATEGORY = "INIT_AMOUNT";

/** Candidate header rows to inspect before giving up. */
const HEADER_SCAN_LIMIT = 5;

const COMPACT_DATE = /^(\d{4})(\d{2})(\d{2})$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_MONTH = 12;
const MAX_DAY = 31;

function normalizeDate(raw: string): string | null {
  const value = raw.trim();
  if (ISO_DATE.test(value)) {
    return value;
  }
  const match = COMPACT_DATE.exec(value);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const monthNum = Number(month);
  const dayNum = Number(day);
  if (monthNum < 1 || monthNum > MAX_MONTH || dayNum < 1 || dayNum > MAX_DAY) {
    return null;
  }
  return `${year}-${month}-${day}`;
}

const COMPACT_TIME = /^(\d{1,2})(\d{2})$/;
const COLON_TIME = /^(\d{1,2}):(\d{2})$/;
const MAX_HOURS = 23;
const MAX_MINUTES = 59;
const TWO_DIGITS = 2;

function normalizeTime(raw: string | undefined): string | null {
  const value = raw?.trim() ?? "";
  if (!value) {
    return null;
  }
  const match = COLON_TIME.exec(value) ?? COMPACT_TIME.exec(value);
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > MAX_HOURS || minutes > MAX_MINUTES) {
    return null;
  }
  return `${String(hours).padStart(TWO_DIGITS, "0")}:${String(minutes).padStart(TWO_DIGITS, "0")}`;
}

/** "1.234,56" / "1,234.56" / "12.5" → decimal number, or null if it cannot be parsed. */
function parseDecimal(raw: string): number | null {
  let value = raw.trim();
  if (!value) {
    return null;
  }
  const hasComma = value.includes(",");
  const hasDot = value.includes(".");
  if (hasComma && hasDot) {
    // The decimal separator is the one furthest to the right.
    value =
      value.lastIndexOf(",") > value.lastIndexOf(".")
        ? value.replaceAll(".", "").replace(",", ".")
        : value.replaceAll(",", "");
  } else if (hasComma) {
    value = value.replace(",", ".");
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function emptyToNull(raw: string | undefined): string | null {
  const value = raw?.trim() ?? "";
  return value ? value : null;
}

// ─── Main parser ──────────────────────────────────────────────────────────────

interface HeaderIndex {
  currency: number;
  amount: number;
  category: number;
  subCategory: number;
  date: number;
  expense: number;
  income: number;
  note: number;
  project: number;
  payee: number;
  time: number;
}

function buildHeaderIndex(cells: readonly string[]): HeaderIndex {
  const normalized = cells.map((c) => c.trim().toLowerCase());
  const find = (predicate: (cell: string) => boolean): number => normalized.findIndex(predicate);
  return {
    currency: find((c) => c === "currency"),
    amount: find((c) => c === "amount"),
    category: find((c) => c === "category"),
    subCategory: find((c) => c === "sub-category" || c === "subcategory"),
    date: find((c) => c === "date"),
    expense: find((c) => c.startsWith("expense")),
    income: find((c) => c.startsWith("income")),
    note: find((c) => c === "note"),
    project: find((c) => c === "project"),
    payee: find((c) => c.startsWith("payee")),
    time: find((c) => c === "time"),
  };
}

function cellAt(record: readonly string[], index: number): string | undefined {
  return index === -1 ? undefined : record[index];
}

interface AccountAccumulator {
  name: string;
  currency: string;
  initialBalanceMinor: number;
}

/**
 * Parses the full text of an AndroMoney export. It never throws: per-row problems
 * go to `issues` (with an approximate 1-based CSV line number).
 */
export function parseAndroMoneyCsv(text: string): AndroMoneyImportPlan {
  const issues: CsvImportIssue[] = [];
  const rows: AndroMoneyRow[] = [];
  const accountsByName = new Map<string, AccountAccumulator>();
  const categoriesByKey = new Map<string, AndroMoneyCategory>();
  const projects = new Set<string>();

  const records = parseCsvDocument(stripBom(text));

  // Locate the header (there may be an "AndroMoney,vX" preamble before it).
  let headerRow = -1;
  let headerCells: string[] | null = null;
  for (let i = 0; i < Math.min(records.length, HEADER_SCAN_LIMIT); i++) {
    const candidate = records[i];
    if (candidate && isAndroMoneyHeader(candidate)) {
      headerRow = i;
      headerCells = candidate;
      break;
    }
  }
  if (headerRow === -1 || !headerCells) {
    issues.push({
      line: 1,
      message: "No se encontró la cabecera de AndroMoney (Currency, Amount, Category…)",
    });
    return {
      rows,
      issues,
      walletsToCreate: [],
      categoriesToCreate: [],
      projectsToCreate: [],
      accounts: [],
      categories: [],
    };
  }

  const header = buildHeaderIndex(headerCells);

  const touchAccount = (name: string, currency: string): AccountAccumulator => {
    const existing = accountsByName.get(name);
    if (existing) {
      return existing;
    }
    const created: AccountAccumulator = { name, currency, initialBalanceMinor: 0 };
    accountsByName.set(name, created);
    return created;
  };

  const touchCategory = (kind: "expense" | "income", name: string, sub: string | null) => {
    const key = `${kind}:${name.toLowerCase()}`;
    let entry = categoriesByKey.get(key);
    if (!entry) {
      entry = { name, kind, subcategories: [] };
      categoriesByKey.set(key, entry);
    }
    if (sub && !entry.subcategories.some((s) => s.toLowerCase() === sub.toLowerCase())) {
      entry.subcategories.push(sub);
    }
  };

  for (let i = headerRow + 1; i < records.length; i++) {
    const record = records[i];
    const line = i + 1;
    // Completely empty rows (common at the end of the export) are ignored.
    if (!record || record.every((cell) => !cell.trim())) {
      continue;
    }

    const currency = cellAt(record, header.currency)?.trim().toUpperCase() ?? "";
    const rawAmount = cellAt(record, header.amount) ?? "";
    const categoryName = cellAt(record, header.category)?.trim() ?? "";
    const subCategory = emptyToNull(cellAt(record, header.subCategory));
    const date = normalizeDate(cellAt(record, header.date) ?? "");
    const expenseAccount = emptyToNull(cellAt(record, header.expense));
    const incomeAccount = emptyToNull(cellAt(record, header.income));

    if (!currency) {
      issues.push({ line, message: "Fila sin divisa" });
      continue;
    }
    const decimalAmount = parseDecimal(rawAmount);
    if (decimalAmount === null) {
      issues.push({ line, message: `Importe ilegible: "${rawAmount}"` });
      continue;
    }
    if (!date) {
      issues.push({
        line,
        message: `Fecha ilegible: "${cellAt(record, header.date) ?? ""}" (se espera YYYYMMDD)`,
      });
      continue;
    }
    if (!expenseAccount && !incomeAccount) {
      issues.push({ line, message: "Fila sin cuenta de origen ni destino" });
      continue;
    }

    const amount = Math.abs(toMinorUnits(decimalAmount, currency));

    // Initial balance: SYSTEM / INIT_AMOUNT. The account usually comes in Income.
    const isInitialBalance =
      categoryName.toUpperCase() === ANDRO_SYSTEM_CATEGORY &&
      (subCategory ?? "").toUpperCase() === ANDRO_INIT_SUBCATEGORY;
    if (isInitialBalance) {
      const accountName = incomeAccount ?? expenseAccount;
      if (accountName) {
        const account = touchAccount(accountName, currency);
        account.initialBalanceMinor += amount;
      }
      continue;
    }

    let kind: AndroMoneyRow["kind"] = "income";
    if (expenseAccount && incomeAccount) {
      kind = "transfer";
    } else if (expenseAccount) {
      kind = "expense";
    }

    if (expenseAccount) {
      touchAccount(expenseAccount, currency);
    }
    if (incomeAccount) {
      touchAccount(incomeAccount, currency);
    }
    if (
      kind !== "transfer" &&
      categoryName &&
      categoryName.toUpperCase() !== ANDRO_SYSTEM_CATEGORY
    ) {
      touchCategory(kind, categoryName, subCategory);
    }
    const projectName = emptyToNull(cellAt(record, header.project));
    if (projectName) {
      projects.add(projectName);
    }

    rows.push({
      currency,
      amount,
      category: categoryName,
      subCategory,
      date,
      time: normalizeTime(cellAt(record, header.time)),
      kind,
      expenseAccount,
      incomeAccount,
      note: emptyToNull(cellAt(record, header.note)),
      project: projectName,
      payee: emptyToNull(cellAt(record, header.payee)),
    });
  }

  const accounts = [...accountsByName.values()];
  const categories = [...categoriesByKey.values()];
  return {
    rows,
    issues,
    walletsToCreate: accounts.map((a) => a.name),
    categoriesToCreate: categories.map((c) => c.name),
    projectsToCreate: [...projects],
    accounts,
    categories,
  };
}

// ─── Plan execution ───────────────────────────────────────────────────────────

/** Stable key for mapping an AndroMoney category+subcategory. */
export function androCategoryKey(
  kind: "expense" | "income",
  name: string,
  sub?: string | null,
): string {
  const base = `${kind}:${name.trim().toLowerCase()}`;
  return sub ? `${base}>${sub.trim().toLowerCase()}` : base;
}

export interface AndroMoneyImportMapping {
  /** AndroMoney account name → existing wallet (id), or null = create. */
  wallets: Record<string, string | null>;
  /** `androCategoryKey(...)` → existing category (id), or null = create. */
  categories: Record<string, string | null>;
  /** Project name → existing project (id); missing ones are created. */
  projects?: Record<string, string>;
}

export interface AndroMoneyImportResult {
  importedTx: number;
  createdWallets: number;
  createdCategories: number;
  createdProjects: number;
  /** Initial balances skipped because they map to an existing wallet. */
  skippedInitialBalances: number;
}

const PROGRESS_EVERY = 50;
const DEFAULT_IMPORT_TIME = "12:00";

/**
 * Executes the plan inside a local transaction (`db.transaction`): creates
 * unmapped wallets/categories/projects and inserts all transactions.
 *
 * Note on initial balances: if the account is CREATED, its `initial_balance`
 * comes from the accumulated INIT_AMOUNT; if it maps to an existing wallet, the
 * CSV's initial balance is ignored (the wallet already has its own) and counted
 * in `skippedInitialBalances`.
 */
export async function runAndroMoneyImport(
  db: PowerSyncDrizzleDB,
  userId: string,
  plan: AndroMoneyImportPlan,
  mapping: AndroMoneyImportMapping,
  onProgress?: (done: number, total: number) => void,
): Promise<AndroMoneyImportResult> {
  const result: AndroMoneyImportResult = {
    importedTx: 0,
    createdWallets: 0,
    createdCategories: 0,
    createdProjects: 0,
    skippedInitialBalances: 0,
  };
  const total = plan.rows.length;

  await db.transaction(async (trx) => {
    const now = nowISO();

    // 1. Wallets
    const walletIdByName = new Map<string, string>();
    for (const account of plan.accounts) {
      const mapped = mapping.wallets[account.name] ?? null;
      if (mapped) {
        walletIdByName.set(account.name, mapped);
        if (account.initialBalanceMinor !== 0) {
          result.skippedInitialBalances += 1;
        }
        continue;
      }
      const id = generateId();
      await trx.insert(wallet).values({
        id,
        user_id: userId,
        name: account.name,
        kind: "cash",
        currency: account.currency,
        initial_balance: account.initialBalanceMinor,
        sort_order: 0,
        archived: 0,
        exclude_from_totals: 0,
        created_at: now,
        updated_at: now,
      });
      walletIdByName.set(account.name, id);
      result.createdWallets += 1;
    }

    // 2. Categories (parent and subcategories)
    const categoryIdByKey = new Map<string, string>();
    for (const cat of plan.categories) {
      const parentKey = androCategoryKey(cat.kind, cat.name);
      let parentId = mapping.categories[parentKey] ?? null;
      if (!parentId) {
        parentId = generateId();
        await trx.insert(category).values({
          id: parentId,
          user_id: userId,
          parent_id: null,
          kind: cat.kind,
          name: cat.name,
          sort_order: 0,
          created_at: now,
          updated_at: now,
        });
        result.createdCategories += 1;
      }
      categoryIdByKey.set(parentKey, parentId);

      for (const sub of cat.subcategories) {
        const subKey = androCategoryKey(cat.kind, cat.name, sub);
        let subId = mapping.categories[subKey] ?? null;
        if (!subId) {
          subId = generateId();
          await trx.insert(category).values({
            id: subId,
            user_id: userId,
            parent_id: parentId,
            kind: cat.kind,
            name: sub,
            sort_order: 0,
            created_at: now,
            updated_at: now,
          });
          result.createdCategories += 1;
        }
        categoryIdByKey.set(subKey, subId);
      }
    }

    // 3. Projects (auto-match by name, create missing ones)
    const projectIdByName = new Map<string, string>(Object.entries(mapping.projects ?? {}));
    for (const name of plan.projectsToCreate) {
      if (projectIdByName.has(name)) {
        continue;
      }
      const id = generateId();
      await trx.insert(project).values({
        id,
        user_id: userId,
        name,
        active: 1,
        created_at: now,
        updated_at: now,
      });
      projectIdByName.set(name, id);
      result.createdProjects += 1;
    }

    // 4. Transactions
    let done = 0;
    for (const row of plan.rows) {
      const sourceAccount = row.kind === "income" ? row.incomeAccount : row.expenseAccount;
      const walletId = sourceAccount ? walletIdByName.get(sourceAccount) : undefined;
      if (!walletId) {
        continue; // el parser garantiza cuenta; defensivo ante mappings rotos
      }
      const toWalletId =
        row.kind === "transfer" && row.incomeAccount
          ? (walletIdByName.get(row.incomeAccount) ?? null)
          : null;
      const categoryId =
        row.kind === "transfer"
          ? null
          : (categoryIdByKey.get(androCategoryKey(row.kind, row.category, row.subCategory)) ??
            categoryIdByKey.get(androCategoryKey(row.kind, row.category)) ??
            null);

      await trx.insert(tx).values({
        id: generateId(),
        user_id: userId,
        kind: row.kind,
        amount: row.amount,
        currency: row.currency,
        base_amount: row.amount,
        base_currency: row.currency,
        wallet_id: walletId,
        to_wallet_id: toWalletId,
        to_amount: row.kind === "transfer" ? row.amount : null,
        category_id: categoryId,
        occurred_at: `${row.date}T${row.time ?? DEFAULT_IMPORT_TIME}:00.000Z`,
        note: row.note,
        payee: row.payee,
        project_id: row.project ? (projectIdByName.get(row.project) ?? null) : null,
        is_adjustment: 0,
        created_at: now,
        updated_at: now,
      });
      result.importedTx += 1;
      done += 1;
      if (onProgress && (done % PROGRESS_EVERY === 0 || done === total)) {
        onProgress(done, total);
      }
    }
  });

  return result;
}
